import { useState } from 'react';
import { useCustom, useCustomMutation, useInvalidate } from '@refinedev/core';
import { notify } from '@/lib/notify';
import {
  Loader2,
  MessageSquare,
  ChevronRight,
  Send,
  Trash2,
  ShieldCheck,
  CornerDownRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Section, formatDate } from '@/components/universal';

interface LessonSummary {
  moduleItemId: number;
  moduleId: number;
  order: number;
  title: string;
  commentsCount: number;
  lastCommentAt: string | null;
}

interface CommentAuthor {
  id: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
}

interface CommentNode {
  id: number;
  authorId: number | null;
  senderAdminId: number | null;
  parentId: number | null;
  content: string;
  createdAt: string;
  author: CommentAuthor;
  isAdmin: boolean;
  replies: CommentNode[];
}

interface CourseCommentsTabProps {
  courseId: string | number;
}

function authorName(author: CommentAuthor): string {
  const name = [author.firstName, author.lastName].filter(Boolean).join(' ').trim();
  if (name) {return name;}
  if (author.username) {return `@${author.username}`;}
  return 'Без имени';
}

function authorInitials(author: CommentAuthor): string {
  const name = authorName(author);
  return name.replace(/^@/, '').slice(0, 2).toUpperCase();
}

export function CourseCommentsTab({ courseId }: CourseCommentsTabProps) {
  const [openLesson, setOpenLesson] = useState<LessonSummary | null>(null);

  const { query } = useCustom<LessonSummary[]>({
    url: `/admin/courses/${courseId}/lesson-comments`,
    method: 'get',
  });

  const lessons: LessonSummary[] = query.data?.data ?? [];
  const isLoading = query.isLoading;
  const totalComments = lessons.reduce((acc, l) => acc + l.commentsCount, 0);

  return (
    <div className="space-y-4 pt-6">
      <Section label={`Уроки-статьи · ${lessons.length} · комментариев: ${totalComments}`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : lessons.length === 0 ? (
          <div className="text-center text-muted-foreground py-12 text-sm">
            В курсе нет уроков-статей с комментариями
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Урок</TableHead>
                <TableHead className="w-[140px]">Комментарии</TableHead>
                <TableHead className="w-[180px]">Последний</TableHead>
                <TableHead className="w-[48px]" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {lessons.map((lesson) => {
                const hasComments = lesson.commentsCount > 0;
                return (
                  <TableRow
                    key={lesson.moduleItemId}
                    className="cursor-pointer"
                    onClick={() => setOpenLesson(lesson)}
                  >
                    <TableCell>
                      <p className="font-medium text-sm">{lesson.title}</p>
                    </TableCell>
                    <TableCell>
                      {hasComments ? (
                        <Badge variant="secondary" className="gap-1">
                          <MessageSquare className="h-3 w-3" />
                          {lesson.commentsCount}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {lesson.lastCommentAt ? formatDate(lesson.lastCommentAt) : '—'}
                    </TableCell>
                    <TableCell>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Section>

      <LessonCommentsSheet
        lesson={openLesson}
        onClose={() => setOpenLesson(null)}
      />
    </div>
  );
}

interface LessonCommentsSheetProps {
  lesson: LessonSummary | null;
  onClose: () => void;
}

function LessonCommentsSheet({ lesson, onClose }: LessonCommentsSheetProps) {
  const invalidate = useInvalidate();
  const lessonId = lesson?.moduleItemId;

  const { query } = useCustom<CommentNode[]>({
    url: `/admin/lessons/${lessonId}/comments`,
    method: 'get',
    queryOptions: { enabled: Boolean(lessonId) },
  });

  const tree: CommentNode[] = query.data?.data ?? [];
  const isLoading = query.isLoading;

  const refresh = () => {
    if (lessonId) {
      invalidate({
        resource: 'courses',
        invalidates: ['all'],
      });
      query.refetch();
    }
  };

  return (
    <Sheet open={Boolean(lesson)} onOpenChange={(open) => { if (!open) {onClose();} }}>
      <SheetContent className="w-full sm:max-w-xl flex flex-col gap-0 p-0">
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle className="text-base">{lesson?.title ?? 'Комментарии'}</SheetTitle>
          <SheetDescription>
            Обсуждение урока. Ответы публикуются от лица администрации.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1">
          <div className="px-6 py-4 space-y-4">
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : tree.length === 0 ? (
              <div className="text-center text-muted-foreground py-12 text-sm">
                Пока нет комментариев
              </div>
            ) : (
              tree.map((node) => (
                <CommentThread
                  key={node.id}
                  node={node}
                  lessonId={lessonId!}
                  onChanged={refresh}
                  depth={0}
                />
              ))
            )}
          </div>
        </ScrollArea>

        {lessonId ? (
          <div className="border-t px-6 py-4">
            <ReplyForm
              url={`/admin/lessons/${lessonId}/comments`}
              placeholder="Написать комментарий от лица администрации…"
              submitLabel="Отправить"
              onSent={refresh}
            />
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

interface CommentThreadProps {
  node: CommentNode;
  lessonId: number;
  onChanged: () => void;
  depth: number;
}

function CommentThread({ node, lessonId, onChanged, depth }: CommentThreadProps) {
  const [replying, setReplying] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMutation = useCustomMutation();
  const { mutate: deleteComment } = deleteMutation;
  const deleting = deleteMutation.mutation.isPending;

  const handleDelete = () => {
    deleteComment(
      { url: `/admin/lesson-comments/${node.id}`, method: 'delete', values: {} },
      {
        onSuccess: () => {
          notify.success('Комментарий удалён');
          setConfirmDelete(false);
          onChanged();
        },
        onError: () => {
          notify.error('Не удалось удалить комментарий');
        },
      },
    );
  };

  return (
    <div className={depth > 0 ? 'border-l pl-4 ml-1' : ''}>
      <div className="flex gap-3">
        <Avatar className="h-8 w-8 shrink-0">
          {node.author.photoUrl ? <AvatarImage src={node.author.photoUrl} /> : null}
          <AvatarFallback className="text-[11px]">{authorInitials(node.author)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{authorName(node.author)}</span>
            {node.isAdmin ? (
              <Badge className="gap-1 bg-primary/10 text-primary text-[10px]">
                <ShieldCheck className="h-3 w-3" />
                Администрация
              </Badge>
            ) : null}
            <span className="text-[11px] text-muted-foreground">{formatDate(node.createdAt)}</span>
          </div>
          <p className="text-sm mt-1 whitespace-pre-wrap break-words">{node.content}</p>
          <div className="flex items-center gap-3 mt-1.5">
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
              onClick={() => setReplying((v) => !v)}
            >
              <CornerDownRight className="h-3 w-3" />
              Ответить
            </button>
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-destructive inline-flex items-center gap-1"
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="h-3 w-3" />
              Удалить
            </button>
          </div>

          {replying ? (
            <div className="mt-3">
              <ReplyForm
                url={`/admin/lesson-comments/${node.id}/reply`}
                placeholder="Ответ от лица администрации…"
                submitLabel="Ответить"
                autoFocus
                onSent={() => {
                  setReplying(false);
                  onChanged();
                }}
                onCancel={() => setReplying(false)}
              />
            </div>
          ) : null}
        </div>
      </div>

      {node.replies.length > 0 ? (
        <div className="mt-3 space-y-3">
          {node.replies.map((child) => (
            <CommentThread
              key={child.id}
              node={child}
              lessonId={lessonId}
              onChanged={onChanged}
              depth={depth + 1}
            />
          ))}
        </div>
      ) : null}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить комментарий?</AlertDialogTitle>
            <AlertDialogDescription>
              Комментарий и все ответы на него будут удалены безвозвратно.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Удалить'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface ReplyFormProps {
  url: string;
  placeholder: string;
  submitLabel: string;
  autoFocus?: boolean;
  onSent: () => void;
  onCancel?: () => void;
}

function ReplyForm({ url, placeholder, submitLabel, autoFocus, onSent, onCancel }: ReplyFormProps) {
  const [content, setContent] = useState('');
  const replyMutation = useCustomMutation();
  const { mutate } = replyMutation;
  const isPending = replyMutation.mutation.isPending;

  const submit = () => {
    const trimmed = content.trim();
    if (trimmed.length === 0) {return;}
    mutate(
      { url, method: 'post', values: { content: trimmed } },
      {
        onSuccess: () => {
          setContent('');
          onSent();
        },
        onError: () => {
          notify.error('Не удалось отправить');
        },
      },
    );
  };

  return (
    <div className="space-y-2">
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={placeholder}
        rows={2}
        autoFocus={autoFocus}
        maxLength={2000}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
        }}
      />
      <div className="flex items-center justify-end gap-2">
        {onCancel ? (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={isPending}>
            Отмена
          </Button>
        ) : null}
        <Button type="button" size="sm" onClick={submit} disabled={isPending || content.trim().length === 0}>
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Send className="h-4 w-4 mr-1.5" />
              {submitLabel}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
